import { Anchor, Box } from "@mantine/core";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cssUnitsToPixels } from "../../utils/css-utils";
import { debounce } from "../../utils/function-utils";

// Fades the bottom of the collapsed content into whatever surface sits behind it,
// so no fade colour has to be kept in sync with each surface.
const FADE_MASK = "linear-gradient(to bottom, black calc(100% - 4rem), transparent)";

interface CollapsibleAreaProps {
  children: React.ReactNode;
  collapseHeight: string; // e.g., "10rem"
}

function CollapsibleArea({ children, collapseHeight }: CollapsibleAreaProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["common"]);
  // Only fade when content is actually being hidden, so complete content is never dimmed.
  const showFade = isOverflowing && !isExpanded;

  const checkOverflow = useCallback(() => {
    if (contentRef.current) {
      const el = contentRef.current;
      const originalMaxHeight = el.style.maxHeight;
      const originalOverflowY = el.style.overflowY;

      try {
        const targetPixelHeight = cssUnitsToPixels(collapseHeight, el);
        el.style.maxHeight = "none";
        const actualComputedOverflowY = window.getComputedStyle(el).overflowY;
        if (actualComputedOverflowY === "hidden") {
          // Temporarily change to 'visible' to ensure scrollHeight is not clipped
          el.style.overflowY = "visible";
        }

        void el.offsetHeight; // Force reflow
        const scrollHeight = el.scrollHeight;

        if (actualComputedOverflowY === "hidden") {
          el.style.overflowY = "hidden"; // Restore to its specific computed value
        }

        setIsOverflowing(scrollHeight > targetPixelHeight);
      } catch (error) {
        console.error("CollapsibleArea: Error during overflow check. Styles might not be fully restored.", error);
      } finally {
        el.style.maxHeight = originalMaxHeight;
        el.style.overflowY = originalOverflowY;
      }
    }
  }, [collapseHeight]);

  useLayoutEffect(() => {
    checkOverflow();

    const handleResize = debounce(() => {
      checkOverflow();
    }, 200);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // Rerun if collapseHeight changes (checkOverflow will be new) or if children change (content might change)
  }, [checkOverflow, children]);

  // Effect to apply styles for expand/collapse animation
  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    // Define the transition end handler within the effect closure
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === element && event.propertyName === "max-height") {
        // Check isExpanded from component state at the time of event
        if (isExpanded && contentRef.current) {
          contentRef.current.style.maxHeight = "none";
        }
        // This specific listener has done its job for this specific transition.
        element.removeEventListener("transitionend", handleTransitionEnd);
      }
    };

    if (isExpanded) {
      const currentRenderedHeight = element.clientHeight;
      element.style.maxHeight = "none";
      const scrollHeight = element.scrollHeight;

      element.style.maxHeight = currentRenderedHeight + "px";
      element.style.overflowY = "hidden";
      void element.offsetHeight; // Force reflow to apply this starting height and overflow.

      requestAnimationFrame(() => {
        if (contentRef.current) {
          // Check ref again in rAF
          contentRef.current.style.maxHeight = scrollHeight + "px";
          contentRef.current.style.overflowY = "visible"; // Or 'auto'
        }
      });

      element.addEventListener("transitionend", handleTransitionEnd);

      return () => {
        element.removeEventListener("transitionend", handleTransitionEnd);
      };
    } else {
      if (element.style.maxHeight === "none" || element.style.maxHeight === "") {
        element.style.maxHeight = element.scrollHeight + "px";
        void element.offsetHeight; // Force reflow to apply scrollHeight as maxHeight.
      }
      requestAnimationFrame(() => {
        if (contentRef.current) {
          // Check ref again in rAF
          contentRef.current.style.maxHeight = collapseHeight;
          contentRef.current.style.overflowY = "hidden";
        }
      });
      return () => {
        // No specific cleanup related to listeners for the collapse case.
      };
    }
  }, [isExpanded, collapseHeight, children]);

  return (
    <Box
      pos="relative"
      // 🔒 EXCEPTION: Overflow management
      style={{ overflow: "hidden" }}
    >
      <Box
        ref={contentRef}
        // 🔒 EXCEPTION: Animation and overflow logic
        style={{
          zIndex: 10,
          position: "relative",
          overflowY: "hidden", // Base style, JS will manage for transitions
          // Initial maxHeight before effects run or for non-JS scenarios
          maxHeight: collapseHeight,
          transition: "max-height 0.2s ease-in-out",
          // 🔒 EXCEPTION: Mask fade over the hidden content
          maskImage: showFade ? FADE_MASK : undefined,
          WebkitMaskImage: showFade ? FADE_MASK : undefined,
        }}
      >
        {children}
      </Box>
      {isOverflowing && (
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          mt="xs"
          // 🔒 EXCEPTION: Flexbox layout & cursor
          style={{
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            width: "100%",
            zIndex: 20,
          }}
        >
          <Anchor
            component="button"
            type="button" // fix for form submission if any
            underline="hover"
            px="md"
            py="xs"
            fz="md"
            // 🔒 EXCEPTION: Button reset & custom styling
            style={{
              border: "none",
              zIndex: 20,
              color: "inherit",
            }}
          >
            {isExpanded ? t("collapsibleArea.showLess") : t("collapsibleArea.showMore")}
          </Anchor>
        </Box>
      )}
    </Box>
  );
}

export default CollapsibleArea;
